import pipefitterApi from '../../../services/pipefitterApi';
import { normalizePipeIdsForGroup } from '../helpers/reportPipeIds';
import { isPipefitterReportType, type PipefitterReportType } from '../constants/reportVersionTypes';
import {
  pickPrimaryReportFileName,
  pickPrimaryReportFileUrl,
} from '../helpers/normalizeReportFileUrl';

export interface ReportVersionRow {
  docId: number;
  docRef: number;
  fileUrl: string;
  fileName: string;
  uploadDate: string | null;
  uploadedBy: string | null;
  spreadId: string | null;
  subSpreadId: string | null;
  pipeCount?: number;
  pipeIds?: string[];
}

export interface ReportVersionsEnvelope {
  success?: number | boolean;
  status?: number;
  message?: string;
  data?: { versions?: unknown[] };
}

export interface GetReportVersionsParams {
  projectNumber: string;
  reportType: PipefitterReportType;
  spread?: string;
  subSpread?: string;
}

export function parseReportVersionsEnvelope(raw: unknown): ReportVersionsEnvelope {
  let body: any = raw;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return { success: false, message: 'Invalid response' };
    }
  }
  if (!body || typeof body !== 'object') {
    return { success: false, message: 'Invalid response' };
  }
  return body as ReportVersionsEnvelope;
}

export function isReportVersionsFailure(envelope: ReportVersionsEnvelope): boolean {
  if (envelope.status === 410) return true;
  const s = envelope.success;
  return s === 0 || s === false;
}

export function normalizeReportVersionRow(raw: any): ReportVersionRow {
  const fileUrlRaw = raw.fileUrl ?? raw.file_url;
  const fileUrl = pickPrimaryReportFileUrl(fileUrlRaw);
  const fileNameFromRef = pickPrimaryReportFileName(fileUrlRaw);

  return {
    docId: Number(raw.docId ?? raw.doc_id ?? 0),
    docRef: Number(raw.docRef ?? raw.doc_ref ?? 0),
    fileUrl,
    fileName: String(raw.fileName ?? raw.file_name ?? fileNameFromRef ?? 'Report.pdf'),
    uploadDate: (raw.uploadDate ?? raw.upload_date ?? null) as string | null,
    uploadedBy: (raw.uploadedBy ?? raw.uploaded_by ?? null) as string | null,
    spreadId:
      raw.spreadId != null ? String(raw.spreadId) : raw.spread_id != null ? String(raw.spread_id) : null,
    subSpreadId:
      raw.subSpreadId != null
        ? String(raw.subSpreadId)
        : raw.sub_spread_id != null
          ? String(raw.sub_spread_id)
          : null,
    pipeCount:
      raw.pipeCount != null
        ? Number(raw.pipeCount)
        : raw.pipe_count != null
          ? Number(raw.pipe_count)
          : undefined,
    pipeIds: normalizePipeIdsForGroup(
      raw.pipeIds ?? raw.pipe_ids ?? raw.selectedPipeStatusIds ?? raw.selected_pipe_status_ids
    ),
  };
}

export function buildReportVersionsQuery(params: GetReportVersionsParams): URLSearchParams {
  const projectNumber = params.projectNumber?.trim();
  if (!projectNumber) {
    throw new Error('projectNumber is required to fetch report versions.');
  }
  if (!isPipefitterReportType(params.reportType)) {
    throw new Error(`Invalid reportType "${String(params.reportType)}" for report versions request.`);
  }

  const query = new URLSearchParams({
    projectNumber,
    reportType: params.reportType,
  });

  if (params.spread?.trim()) query.set('spread', params.spread.trim());
  if (params.subSpread?.trim()) query.set('subSpread', params.subSpread.trim());
  return query;
}

export interface GetSequenceReportVersionsParams {
  projectNumber: string;
  spread?: string;
  subSpread?: string;
}

export function buildSequenceReportVersionsQuery(params: GetSequenceReportVersionsParams): URLSearchParams {
  const projectNumber = params.projectNumber?.trim();
  if (!projectNumber) {
    throw new Error('projectNumber is required to fetch sequence report versions.');
  }
  const query = new URLSearchParams({ projectNumber });
  if (params.spread?.trim()) query.set('spread', params.spread.trim());
  if (params.subSpread?.trim()) query.set('subSpread', params.subSpread.trim());
  return query;
}

/** GET `/reports/sequence-report-versions` — sequence PDF versions for a project scope. */
export async function getSequenceReportVersions(
  params: GetSequenceReportVersionsParams
): Promise<ReportVersionsEnvelope> {
  const query = buildSequenceReportVersionsQuery(params).toString();
  const res = await pipefitterApi.get(`/reports/sequence-report-versions?${query}`, {
    headers: { Accept: 'application/json' },
  });
  return parseReportVersionsEnvelope(res.data);
}

export async function getReportVersions(params: GetReportVersionsParams): Promise<ReportVersionsEnvelope> {
  const query = buildReportVersionsQuery(params).toString();
  const res = await pipefitterApi.get(`/reports/report-versions?${query}`, {
    headers: { Accept: 'application/json' },
  });
  return parseReportVersionsEnvelope(res.data);
}
